'use client'

import LoginModal from '@/features/auth/LoginModal'
import { IHIForm, IHIFormType } from '@/shared/components/hi/IHIForm'
import {
  MedicareForm,
  MedicareFormType,
} from '@/shared/components/hi/MedicareForm'
import HealthcareIdentifierForm from '@/shared/components/hi/HealthcareIdentifierForm'
import { FormProps } from '@/shared/components/hi/types'
import {
  softEditorialEase,
  softMotionDuration,
} from '@/shared/components/motion/motion-tokens'
import Spinner from '@/shared/components/Spinner'
import FormButtonGroup from '@/shared/components/questionnaires/FormButtonGroup'
import { IHIVerifyFormContext } from '@/shared/request/IHIVerifyFormContext'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import React, { useContext, useState } from 'react'
import { useStore } from 'zustand'

type Props = {
  serviceLabel?: string
  mobileServiceLabel?: string
  title?: string
  description?: string
  currentStep?: number
  totalSteps?: number
}

const headerListMotion = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.08,
      staggerChildren: 0.075,
    },
  },
}

const headerItemMotion = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: softMotionDuration.panel,
      ease: softEditorialEase,
    },
  },
}

export default function IHIVerifyFormStage({
  serviceLabel = 'Request care · General consultation',
  mobileServiceLabel = 'Request care · Consultation',
  title = 'Add your Medicare details.',
  description = 'The doctor needs this to issue prescriptions in your name — it stays between you and your doctor.',
  currentStep,
  totalSteps,
}: Props) {
  const store = useContext(IHIVerifyFormContext)
  if (!store)
    throw new Error(
      'IHIVerifyFormStage must be used within a IHIVerifyFormContextProvider',
    )

  const [showLoginDialog, setShowLoginDialog] = useState(false)
  const reduceMotion = useReducedMotion()

  const session = useStore(store, (s) => s.session)
  const ihiVerificationState = useStore(store, (s) => s.ihiVerificationState)
  const updateIHIVerificationState = useStore(
    store,
    (s) => s.updateIHIVerificationState,
  )
  const onPrev = useStore(store, (s) => s.onPrev)
  const onNext = useStore(store, (s) => s.onNext)
  const completeVerifiedIdentifierAttachment = useStore(
    store,
    (s) => s.completeVerifiedIdentifierAttachment,
  )

  return (
    <motion.main
      animate={{ opacity: 1, y: 0 }}
      className="min-h-[100dvh] min-h-screen w-full bg-white pt-[66px] text-[#0B0B0F] lg:pt-[86px]"
      initial={reduceMotion ? false : { opacity: 0, y: 18 }}
      transition={
        reduceMotion
          ? { duration: 0 }
          : {
              duration: softMotionDuration.handoff,
              ease: softEditorialEase,
            }
      }
    >
      <div className="mx-auto flex w-full max-w-[680px] flex-col px-5 pb-12 pt-10 sm:px-6 sm:pb-[72px] sm:pt-[72px] lg:px-0">
        <motion.header
          animate="visible"
          className="flex flex-col gap-3 sm:gap-4"
          initial={reduceMotion ? false : 'hidden'}
          variants={headerListMotion}
        >
          <motion.div
            className="flex items-center justify-between gap-5"
            variants={headerItemMotion}
          >
            <div className="flex min-w-0 items-center gap-2">
              <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-[#2563EB] max-sm:h-1.5 max-sm:w-1.5" />
              <span className="truncate text-[11.5px] font-semibold uppercase leading-[14px] tracking-[0.025em] text-[#1E3A6E] sm:hidden">
                {mobileServiceLabel}
              </span>
              <span className="hidden truncate text-[13px] font-semibold uppercase leading-4 tracking-[0.025em] text-[#1E3A6E] sm:inline">
                {serviceLabel}
              </span>
            </div>
            {currentStep && totalSteps && (
              <span className="shrink-0 text-[10.5px] font-semibold uppercase leading-[13px] tracking-[0.06em] text-[#A0A0A8] sm:text-[11.5px] sm:leading-[14px]">
                Step {currentStep} of {totalSteps}
              </span>
            )}
          </motion.div>

          <motion.h1
            className="h-[66px] shrink-0 font-jakarta text-[26px] font-bold leading-[33px] tracking-[-0.02em] sm:h-auto sm:text-[34px] sm:leading-[42px]"
            variants={headerItemMotion}
          >
            {title}
          </motion.h1>

          <motion.p
            className="max-w-[620px] text-[14px] leading-[21px] text-[#82828B] sm:text-[14.5px] sm:leading-[22px]"
            variants={headerItemMotion}
          >
            {description}
          </motion.p>

          <AnimatePresence initial={!reduceMotion}>
            {!session && (
              <motion.aside
                animate={{ opacity: 1, y: 0 }}
                className="mt-1.5 flex items-center gap-3 rounded-[14px] bg-[#F4F4F6] px-3.5 py-3 sm:mt-1 sm:gap-4 sm:px-4 sm:py-[13px]"
                exit={
                  reduceMotion
                    ? undefined
                    : { opacity: 0, y: -5, transition: { duration: 0.34 } }
                }
                initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                transition={
                  reduceMotion
                    ? { duration: 0 }
                    : {
                        duration: softMotionDuration.panel,
                        ease: softEditorialEase,
                      }
                }
              >
                <div className="flex min-w-0 flex-col gap-px">
                  <h2 className="text-[13px] font-semibold leading-[17px] sm:text-[13.5px] sm:leading-[18px]">
                    Used NextClinic before?
                  </h2>
                  <p className="text-[12px] leading-4 text-[#82828B] sm:text-[12.5px] sm:leading-[17px]">
                    Your Medicare details are already saved
                    <span className="max-sm:hidden"> to your account</span>.
                  </p>
                </div>
                <button
                  className="ml-auto shrink-0 rounded-full bg-[#0B0B0F] px-4 py-[9px] text-[12.5px] font-semibold leading-[15px] text-white transition-colors duration-300 hover:bg-[#242428] sm:px-[18px] sm:text-[13px] sm:leading-4"
                  onClick={() => setShowLoginDialog(true)}
                  type="button"
                >
                  Sign in
                </button>
              </motion.aside>
            )}
          </AnimatePresence>
        </motion.header>

        <HealthcareIdentifierForm
          session={session}
          state={ihiVerificationState}
          variant="quiz"
          onUpdate={updateIHIVerificationState}
          onVerified={(attachedToProfile) => {
            if (attachedToProfile) {
              // Saving succeeded. Reconcile status and stages immediately so
              // personal details cannot be blocked by stale server props.
              completeVerifiedIdentifierAttachment(null)
            } else {
              onNext()
            }
          }}
          onClickLogin={() => setShowLoginDialog(true)}
          ihiForm={(props) => (
            <StageIHIForm {...props} onBack={onPrev} variant="quiz" />
          )}
          medicareForm={(props) => (
            <StageMedicareForm {...props} onBack={onPrev} variant="quiz" />
          )}
        />

        <LoginModal
          open={showLoginDialog}
          onClose={() => setShowLoginDialog(false)}
        />
      </div>
    </motion.main>
  )
}

type StageFormProps<T> = FormProps<T> & {
  onBack: () => void
  variant?: 'default' | 'quiz'
  duplicateAccountError?: boolean
  onClearServerError?: () => void
  onClickLogin?: () => void
}

function StageIHIForm({
  onVerify,
  state,
  onInputChanged,
  onBack,
  variant = 'default',
  duplicateAccountError,
  onClearServerError,
  onClickLogin,
}: StageFormProps<IHIFormType>) {
  return (
    <IHIForm
      state={state}
      onVerify={onVerify}
      onInputChanged={onInputChanged}
      variant={variant}
      duplicateAccountError={duplicateAccountError}
      onClearServerError={onClearServerError}
      onClickLogin={onClickLogin}
    >
      {(props) =>
        variant === 'quiz' ? (
          <QuizFormActions
            backDisabled={props.isSubmitting}
            nextDisabled={Boolean(duplicateAccountError)}
            submitting={props.isSubmitting}
            onBack={onBack}
          />
        ) : (
          <FormButtonGroup
            backButtonState={props.isSubmitting ? 'disabled' : 'enabled'}
            nextButtonState="hidden"
            submitButtonState={duplicateAccountError ? 'disabled' : 'enabled'}
            onBackButtonClick={onBack}
            onNextButtonClick={() => {}}
            submitButtonLabel="Next"
          />
        )
      }
    </IHIForm>
  )
}

function StageMedicareForm({
  onVerify,
  onBack,
  onInputChanged,
  state,
  variant = 'default',
  duplicateAccountError,
  onClearServerError,
  onClickLogin,
}: StageFormProps<MedicareFormType>) {
  return (
    <MedicareForm
      state={state}
      onInputChanged={onInputChanged}
      onVerify={onVerify}
      variant={variant}
      duplicateAccountError={duplicateAccountError}
      onClearServerError={onClearServerError}
      onClickLogin={onClickLogin}
    >
      {(props) =>
        variant === 'quiz' ? (
          <QuizFormActions
            backDisabled={props.isSubmitting}
            nextDisabled={Boolean(duplicateAccountError)}
            submitting={props.isSubmitting}
            onBack={onBack}
          />
        ) : (
          <FormButtonGroup
            backButtonState={props.isSubmitting ? 'disabled' : 'enabled'}
            nextButtonState="hidden"
            submitButtonState={duplicateAccountError ? 'disabled' : 'enabled'}
            onBackButtonClick={onBack}
            onNextButtonClick={() => {}}
            submitButtonLabel="Next"
          />
        )
      }
    </MedicareForm>
  )
}

function QuizFormActions({
  backDisabled,
  nextDisabled,
  submitting,
  onBack,
}: {
  backDisabled: boolean
  nextDisabled: boolean
  submitting: boolean
  onBack: () => void
}) {
  const reduceMotion = useReducedMotion()
  const disabled = nextDisabled || submitting

  return (
    <motion.div
      animate={{ opacity: 1 }}
      className="flex flex-col gap-2.5 pb-0 pt-6 sm:flex-row sm:items-center sm:justify-center sm:gap-3 sm:pt-9"
      initial={reduceMotion ? false : { opacity: 0 }}
      transition={
        reduceMotion
          ? { duration: 0 }
          : {
              delay: 0.14,
              duration: softMotionDuration.handoff,
              ease: softEditorialEase,
            }
      }
    >
      <button
        className="order-2 flex h-[47px] w-full items-center justify-center gap-[9px] rounded-full border-[1.5px] border-[#0B0B0F38] px-[26px] text-[14.5px] font-semibold leading-[18px] transition-colors duration-300 hover:border-[#0B0B0F70] disabled:cursor-not-allowed disabled:opacity-40 sm:order-1 sm:w-auto"
        disabled={backDisabled || submitting}
        onClick={onBack}
        type="button"
      >
        <ArrowIcon direction="left" />
        Back
      </button>
      <button
        className="order-1 flex h-[47px] w-full min-w-[106px] items-center justify-center gap-[9px] rounded-full bg-[#0B0B0F] px-7 text-[14.5px] font-semibold leading-[18px] text-white transition-[background-color,opacity] duration-300 hover:bg-[#242428] disabled:cursor-not-allowed disabled:bg-[#0B0B0F] disabled:opacity-35 sm:order-2 sm:w-auto"
        disabled={disabled}
        type="submit"
      >
        {submitting ? <Spinner /> : 'Next'}
        {!submitting && <ArrowIcon direction="right" />}
      </button>
    </motion.div>
  )
}

function ArrowIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      aria-hidden="true"
      className={direction === 'right' ? 'rotate-180' : undefined}
      fill="none"
      height="15"
      viewBox="0 0 24 24"
      width="15"
    >
      <path
        d="M19 12H5M12 19l-7-7 7-7"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.4"
      />
    </svg>
  )
}
